import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import useGeneralHook from "../../../common/hook/useGeneralHook";
import useSWR from "swr";
import MonitoringHeader from "../component/MonitoringLedHeader";
import MonitoringTable from "../component/MonitoringLedTable";
import MonitoringFooter from "../component/MonitoringLedFooter";
import MonitoringImageHeader from "../component/MonitoringImageHeader";
import MonitoringImageFooter from "../component/MonitoringImageFooter";

interface Station {
  name: string;
}

interface StationData {
  name: string;
  data: any[];
  station: Station;
  time: string;
  nreTypeName: string;
  qcvnName: string;
  agentName: string;
  indicatorUnit: string;
}

type DisplayMode = "stations" | "images";

const MonitoringUnifiedPage = () => {
  const [stationsData, setStationsData] = useState<StationData[]>([]);
  const [currentStationIndex, setCurrentStationIndex] = useState(0);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("stations");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const { dispatch, API_PATHS, fetchThunk, enqueueSnackbar } = useGeneralHook();

  const { data: dataLatest } = useSWR(
    API_PATHS.monitoring.indexForDataLedLatest,
    async (url: string) => {
      const json = await dispatch(fetchThunk(url));
      return json?.data;
    },
    { revalidateOnFocus: false, refreshInterval: 60000 }
  );

  const { data: dataTime } = useSWR(
    API_PATHS.monitoring.timeSwitch,
    async (url: string) => {
      const json = await dispatch(fetchThunk(url));
      return json?.data;
    },
    { revalidateOnFocus: false }
  );

  const captureList: string[] = dataTime?.capture || [];
  const timeSwitch = typeof dataTime?.timeSwitch === "number" ? dataTime.timeSwitch : 30;
  const isCapture = !!dataTime?.isCapture;

  const getFile = async (filePath: string) => {
    if (imageUrls[filePath]) return imageUrls[filePath];
    setLoading(true);
    try {
      const json = await dispatch(
        fetchThunk(
          API_PATHS.upload.getFile(filePath),
          "get",
          undefined,
          "application/octet-stream"
        )
      );
      if (json.status !== 200) return undefined;
      const url = window.URL.createObjectURL(new Blob([json.blob]));
      setImageUrls((prev) => ({ ...prev, [filePath]: url }));
      return url;
    } catch (e) {
      enqueueSnackbar?.({
        message: "Tải ảnh thất bại",
        variant: "error",
      });
      return undefined;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dataLatest) {
      setStationsData(dataLatest);
      setCurrentStationIndex(0);
    }
  }, [dataLatest]);

  // Xử lý khi hoàn thành hiển thị tất cả các trạm
  const handleAllStationsShown = () => {
    if (captureList.length > 0 && isCapture) {
      setDisplayMode("images");
      setCurrentImageIndex(0);
      // Preload ảnh đầu tiên
      if (captureList[0] && !imageUrls[captureList[0]]) {
        getFile(captureList[0]);
      }
    } else {
      // Nếu không có ảnh, quay lại hiển thị trạm đầu tiên
      setDisplayMode("stations");
      setCurrentStationIndex(0);
    }
  };

  // Xử lý khi hoàn thành hiển thị tất cả ảnh
  const handleAllImagesShown = () => {
    setDisplayMode("stations");
    setCurrentStationIndex(0);
  };

  // Effect quản lý interval cho chuyển đổi giữa các trạm
  useEffect(() => {
    if (displayMode !== "stations" || !dataTime || stationsData.length === 0) return;

    const interval = setInterval(() => {
      if (currentStationIndex < stationsData.length - 1) {
        setCurrentStationIndex(prev => prev + 1);
      } else {
        handleAllStationsShown();
      }
    }, timeSwitch * 1000);

    return () => clearInterval(interval);
  }, [displayMode, currentStationIndex, stationsData.length, dataTime, timeSwitch]);

  // Effect quản lý interval cho chuyển đổi giữa các ảnh
  useEffect(() => {
    if (displayMode !== "images" || !dataTime || captureList.length === 0) return;

    const interval = setInterval(() => {
      if (currentImageIndex < captureList.length - 1) {
        // Preload ảnh tiếp theo
        if (captureList[currentImageIndex + 1] && !imageUrls[captureList[currentImageIndex + 1]]) {
          getFile(captureList[currentImageIndex + 1]);
        }
        setCurrentImageIndex(prev => prev + 1);
      } else {
        handleAllImagesShown();
      }
    }, timeSwitch * 1000);

    return () => clearInterval(interval);
  }, [displayMode, currentImageIndex, captureList.length, dataTime, timeSwitch]);

  const currentStation = stationsData[currentStationIndex];
  const currentData = currentStation?.data || [];
  const currentImageId = captureList[currentImageIndex];
  const imageSrc = currentImageId && imageUrls[currentImageId];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 90px)",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          width: "calc(100% - 40px)",
          zIndex: 1,
          height: "20vh",
        }}
      >
        {displayMode === "images" ? (
          <MonitoringImageHeader stationData={currentStation} />
        ) : (
          <MonitoringHeader stationData={currentStation} />
        )}
      </Box>

      <Box
        sx={{
          height: "70vh",
          overflow: "hidden",
          marginTop: 8,
          "@media (max-width: 800px)": { marginTop: 4, marginBottom: 0 },
        }}
      >
        {displayMode === "images" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {loading || !imageSrc ? (
              <CircularProgress sx={{ color: "white" }} />
            ) : (
              <img
                src={imageSrc}
                alt="Monitoring capture"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  border: "2px solid white",
                }}
              />
            )}
          </Box>
        ) : (
          <MonitoringTable 
            data={currentData} 
            onLastPageReached={() => {
              if (currentStationIndex < stationsData.length - 1) {
                setCurrentStationIndex(prev => prev + 1);
              } else {
                handleAllStationsShown();
              }
            }} 
          />
        )}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          width: "100%",
          zIndex: 1,
          height: "10vh",
        }}
      >
        {displayMode === "images" ? (
          <MonitoringImageFooter stationData={currentStation} />
        ) : (
          <MonitoringFooter stationData={currentStation} />
        )}
      </Box>
    </Box>
  );
};

export default MonitoringUnifiedPage;